import { skipToken } from "@reduxjs/toolkit/query/react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressBar } from "primereact/progressbar";
import { TabView, TabPanel } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { getAPIUrl } from "../../../config/api";
import { RENDERING_TAXONOMY_OPTIONS } from "../../../constants/renderingTaxonomies";
import { parseDate } from "../../../helpers/date";
import {
  IDirectUploadProgress,
  useDirectEdiUpload,
} from "../../../helpers/ediDirectUpload";
import { api } from "../../../store/queries";
import {
  useGetAllEdiConversionFilesQuery,
  useGetEdiConversionFileByIdQuery,
  useConvertAndUploadEdiMutation,
  useDeleteEdiConversionFileMutation,
  useProcessHeldEdiFileMutation,
  useReprocessEdiFileMutation,
  useRunAiAnalysisMutation,
} from "../../../store/queries/ediConversion";
import { useGetAllPayersQuery } from "../../../store/queries/payers";
import { useGetAllProvidersQuery } from "../../../store/queries/providers";
import { authSelector } from "../../../store/slices/auth";
import { userSelector } from "../../../store/slices/user";
import {
  ClaimProcessingStatus,
  EdiCsvDownloadKind,
  IEdiConversionFile,
  IEdiUploadMapping,
  IPayerTaxonomyRule,
  EdiClaimType,
  PlaceOfServiceCategory,
} from "../../../types/ediConversionFile";
import { IProvider, SpecialtyList } from "../../../types/Provider/Provider";
import Edi837IngestsTable from "./Edi837IngestsTable";
import {
  canSplitEdiFile,
  EDI_POLL_INTERVAL_MS,
  isEdiFileInFlight,
  isFlatCsvSkipped,
  isHeld,
  isSplitBatchActive,
} from "./ediFileStatus";
import EdiFileStatusLabel from "./EdiFileStatusLabel";
import { validateEdiMappingSet } from "./ediMappingValidation";
import EdiSplitFilesTab from "./EdiSplitFilesTab";
import EditMappingModal from "./EditMappingModal";
import ReprocessAllModal from "./ReprocessAllModal";
import SplitEdiFileDialog from "./SplitEdiFileDialog";
import { useEdiSplits } from "./useEdiSplits";

const TERMINAL_STATUSES: ClaimProcessingStatus[] = [
  "completed",
  "failed",
  "skipped",
];

const isTerminal = (status?: ClaimProcessingStatus | null) =>
  !!status && TERMINAL_STATUSES.includes(status);

// Sub-tabs of the EDI Files tab. "Split 835 Files" (CLE-2921) is appended, so the first two keep
// their positions.
const FILES_TAB_INDEX = 0;
const SPLIT_TAB_INDEX = 2;

// Tooltip on the actions a held file cannot run yet (CLE-2921).
const HELD_TOOLTIP = "Process the file first";

// CLE-2940 — a file over 10,000 claims has no flat CSV, so its CSV button serves the aggregated CSV.
const CSV_TOOLTIP = "Download 835 CSV";
const AGGREGATED_CSV_TOOLTIP =
  "Download aggregated CSV (the flat CSV is not generated for files over 10,000 claims)";

// CLE-2236 — upload-time claim-type → provider mapping rules.
const EDI_CLAIM_TYPE_OPTIONS: { value: EdiClaimType; label: string }[] = [
  { value: "INSTITUTIONAL", label: "Institutional" },
  { value: "PROFESSIONAL_ANESTHESIA", label: "Professional — Anesthesia" },
  { value: "PROFESSIONAL_PROVIDER", label: "Professional — Provider" },
];

// Max mapping rows the modal allows: INSTITUTIONAL + PROFESSIONAL_ANESTHESIA once each, plus one
// PROFESSIONAL_PROVIDER rule per POS bucket the UI exposes (Facility / Non-Facility) — CLE-2424.
const MAX_MAPPINGS = EDI_CLAIM_TYPE_OPTIONS.length + 1;

// The place-of-service sub-filter applies only to the generic-provider rule. It's surfaced as two
// buckets — "Facility" (= inpatient + outpatient facility) and "Non-Facility" (office/home) — over
// the API's three-value vocabulary. (CLE-2236 follow-up, per Ani.)
const FACILITY_POS_CATEGORIES: PlaceOfServiceCategory[] = [
  "Inpatient",
  "Outpatient Facility",
];
const NON_FACILITY_POS_CATEGORIES: PlaceOfServiceCategory[] = ["Non-Facility"];

// Only the Professional — Provider rule gets a place-of-service sub-filter.
const posFilterAppliesTo = (t: EdiClaimType) => t === "PROFESSIONAL_PROVIDER";

// Provider specialties that may be picked for a given claim type (CLE-2236).
// Professional — Provider and Professional — Anesthesia both also accept FACILITY providers
// (some clients route the professional/generic-provider or anesthesia slice of a remit to a
// facility provider record). For Professional — Provider, ANESTHESIOLOGIST stays excluded since
// it has its own Professional — Anesthesia rule. The backend mirrors this in
// validateUploadMappings (anesthesia accepts ANESTHESIOLOGIST or FACILITY; the generic-provider
// rule rejects only ANESTHESIOLOGIST).
const allowedSpecialtiesForClaimType = (
  claimType: EdiClaimType
): SpecialtyList[] => {
  if (claimType === "INSTITUTIONAL") return [SpecialtyList.FACILITY];
  if (claimType === "PROFESSIONAL_ANESTHESIA")
    return [SpecialtyList.FACILITY, SpecialtyList.ANESTHESIOLOGIST];
  return [
    SpecialtyList.FACILITY,
    SpecialtyList.PATHOLOGIST,
    SpecialtyList.PRIMARY_PROVIDER,
    SpecialtyList.ASSISTANT_PROVIDER,
  ];
};

const newMappingForType = (claimType: EdiClaimType): IEdiUploadMapping => ({
  claimType,
  providerId: "",
  placeOfServiceFilter: [],
});

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 1.5rem;
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
  }

  .required {
    color: #e74c3c;
    margin-left: 0.25rem;
  }
`;

const FileInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  margin-top: 0.5rem;

  .file-name {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .file-details {
    color: #6c757d;
    font-size: 0.875rem;
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

function EdiConversionFilesTable() {
  const toast = useRef<Toast>(null);
  const dispatch = useDispatch();
  const { token } = useSelector(authSelector);
  const { currentAccount } = useSelector(userSelector);

  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [reprocessAllVisible, setReprocessAllVisible] = useState(false);
  // CLE-2479 — edit-mapping dialog for an already-processed file.
  const [editFile, setEditFile] = useState<IEdiConversionFile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFiles837, setSelectedFiles837] = useState<File[]>([]);
  const [mappings, setMappings] = useState<IEdiUploadMapping[]>([
    newMappingForType("INSTITUTIONAL"),
  ]);
  const [searchValue, setSearchValue] = useState("");
  // CLE-2491 — client-side "temp filter" so Ani can cut through the daily Heritage
  // SFTP mass and see just two Specialist Office rates files instead of the whole
  // list. Purely cosmetic (filters the already-fetched list); persisted per-browser
  // so the choice survives a refresh.
  const [showTwoFilesOnly, setShowTwoFilesOnly] = useState(
    () => localStorage.getItem("ediShowTwoFilesOnly") === "true"
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<IDirectUploadProgress | null>(null);
  const [pollingFileId, setPollingFileId] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(FILES_TAB_INDEX);
  // CLE-2921 — the split dialog, opened from the Split tab or pre-selected from a file's row action.
  const [splitDialogVisible, setSplitDialogVisible] = useState(false);
  const [splitInitialFile, setSplitInitialFile] =
    useState<IEdiConversionFile | null>(null);

  const { data: ediConversionFilesData, isLoading } =
    useGetAllEdiConversionFilesQuery({
      search: searchValue,
    });
  // Detail-level polling runs alongside the list polling: it's the reliable
  // source we use to fire completion/failure toasts at the right moment,
  // even when the list cache hasn't caught up yet.
  const { data: pollingData } = useGetEdiConversionFileByIdQuery(
    pollingFileId ?? skipToken,
    {
      // 5 s cadence is a compromise: fast enough that the UI doesn't feel stale during a
      // multi-stage parse, slow enough to stay under the API's per-user rate limit when the
      // list refresh below is also firing.
      pollingInterval: 5000,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payersData } = useGetAllPayersQuery();
  const [convertAndUpload] = useConvertAndUploadEdiMutation();
  const { upload: directUpload } = useDirectEdiUpload();
  const [deleteFile] = useDeleteEdiConversionFileMutation();
  const [reprocessFile] = useReprocessEdiFileMutation();
  const [runAiAnalysis] = useRunAiAnalysisMutation();
  const [processHeldFile] = useProcessHeldEdiFileMutation();
  // CLE-2921 — subscribed here (not only in the Split tab) so a split that finishes while another
  // sub-tab is open still refreshes this list: its source file is replaced by the pieces.
  const { batches: splitBatches, isLoaded: splitsLoaded } = useEdiSplits();

  const ediConversionFiles = ediConversionFilesData?.data || [];

  // CLE-2491 — resolve the provider name for a file so we can tell the two Heritage
  // Specialist categories apart. "(Facility)" is matched with its parenthesis so it
  // never accidentally catches the "(Non-Facility)" office files.
  const providerNameOf = (file: IEdiConversionFile): string =>
    (
      providers?.data?.find((p: any) => p.id === file.providerId)?.name ?? ""
    ).toLowerCase();
  const isOfficeRateFile = (file: IEdiConversionFile): boolean => {
    const dn = (file.displayName ?? "").toLowerCase();
    return (
      providerNameOf(file).includes("office rates") ||
      dn.includes("(non-facility)")
    );
  };
  const isFacilityRateFile = (file: IEdiConversionFile): boolean => {
    const dn = (file.displayName ?? "").toLowerCase();
    return (
      providerNameOf(file).includes("facility rates") ||
      dn.includes("(facility)")
    );
  };

  // How far a record count sits from a target band (0 when inside it).
  const distanceToBand = (count: number, low: number, high: number): number => {
    if (count < low) return low - count;
    if (count > high) return count - high;
    return 0;
  };

  // Pick the file in a category whose record count is closest to the target band —
  // preferring one inside the band, but never hiding the category entirely just
  // because nothing lands in range (e.g. no 1,000–2,000-row Facility file today).
  // `ediConversionFiles` arrives newest-first (API orders by createdAt desc) and the
  // strict `<` keeps that newest file on ties.
  const closestInCategory = (
    predicate: (f: IEdiConversionFile) => boolean,
    low: number,
    high: number
  ): IEdiConversionFile | undefined => {
    const inCategory = ediConversionFiles.filter(predicate);
    if (inCategory.length === 0) return undefined;
    return inCategory.reduce((best, f) =>
      distanceToBand(f.recordCount, low, high) <
      distanceToBand(best.recordCount, low, high)
        ? f
        : best
    );
  };

  // When the "show only 2 files" toggle is on, collapse the whole list down to one
  // file from each category: a Specialist Office rates file nearest the 3,000–4,000-row
  // band and a Specialist Facility rates file nearest the 1,000–2,000-row band.
  const twoFileSample: IEdiConversionFile[] = [
    closestInCategory(isOfficeRateFile, 3000, 4000),
    closestInCategory(isFacilityRateFile, 1000, 2000),
  ].filter((f): f is IEdiConversionFile => !!f);

  const visibleFiles = showTwoFilesOnly ? twoFileSample : ediConversionFiles;

  const handleToggleShowTwo = (checked: boolean) => {
    setShowTwoFilesOnly(checked);
    localStorage.setItem("ediShowTwoFilesOnly", String(checked));
  };

  // Mutators for the upload mapping rules (CLE-2236).
  const updateMapping = (idx: number, patch: Partial<IEdiUploadMapping>) =>
    setMappings((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, ...patch } : m))
    );
  const removeMapping = (idx: number) =>
    setMappings((prev) => prev.filter((_, i) => i !== idx));
  const addMapping = () =>
    setMappings((prev) => {
      const unused = EDI_CLAIM_TYPE_OPTIONS.map((o) => o.value).find(
        (t) => !prev.some((m) => m.claimType === t)
      );
      return [...prev, newMappingForType(unused ?? "PROFESSIONAL_PROVIDER")];
    });

  // CLE-2437 — payer options for the per-payer taxonomy rules. useGetAllPayersQuery returns the
  // full payer set (shape varies: `{ data: [] }` or a bare array), each with { id: number, name }.
  // Resolve to an array defensively so a loading/empty response never throws on .map.
  const payerList: { id: number; name: string }[] = Array.isArray(payersData)
    ? payersData
    : payersData?.data ?? [];
  const payerOptions: { value: number; label: string }[] = payerList
    .map((p: { id: number; name: string }) => ({ value: p.id, label: p.name }))
    .sort((a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label)
    );

  const taxonomyOptions = RENDERING_TAXONOMY_OPTIONS.map((t) => ({
    value: t.code,
    label: `${t.label} (${t.code})`,
  }));

  // CLE-2437 — mutate a single per-payer taxonomy rule within a mapping (rules live on the mapping).
  const updateTaxonomyRules = (
    idx: number,
    rules: IPayerTaxonomyRule[]
  ): void => updateMapping(idx, { payerTaxonomyRules: rules });

  // Cross-rule validation (CLE-2424): claim-type cardinality + professional POS disjointness.
  // Mirrors the backend's validateMappingSet so the user sees the problem before submitting.
  const mappingError = validateEdiMappingSet(mappings);

  // Drive list polling off the list itself, not off pollingFileId. The
  // detail endpoint reports "completed" before the list cache catches
  // up, so tying the refresh interval to pollingFileId stops it one
  // cycle too early and strands the Status badge on "Processing".
  // See isEdiFileInFlight: uploadStatus counts too, and a held file never does.
  const hasInFlightFile = ediConversionFiles.some(isEdiFileInFlight);

  useEffect(() => {
    if (!hasInFlightFile) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      dispatch(api.util.invalidateTags(["EdiConversion"]));
    }, EDI_POLL_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [hasInFlightFile, dispatch]);

  // CLE-2921 — when a split appears, starts, finishes or fails, this list changes underneath us (the
  // source is replaced by its pieces). Refresh it on those transitions, not on every poll.
  const splitSignature = splitBatches
    .map((b) => `${b.id}:${b.status}:${b.pieces.length}`)
    .join("|");
  const prevSplitSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!splitsLoaded) return;
    if (
      prevSplitSignatureRef.current !== null &&
      prevSplitSignatureRef.current !== splitSignature
    ) {
      dispatch(api.util.invalidateTags(["EdiConversion"]));
    }
    prevSplitSignatureRef.current = splitSignature;
  }, [splitSignature, splitsLoaded, dispatch]);

  // Files a split is queued or running for — the API refuses a second split of the same file.
  const splittingFileIds = new Set(
    splitBatches
      .filter(isSplitBatchActive)
      .map((b) => b.sourceEdiConversionFileId)
      .filter((id): id is string => !!id)
  );

  // Fire toasts on per-stage terminal transitions and stop polling once
  // every stage has reached terminal. Driven off the detail-poll cache,
  // which is the reliable signal — the list cache can lag but these
  // toasts will still surface at the right moment.
  const prevStatusesRef = useRef<{
    upload?: IEdiConversionFile["uploadStatus"];
    claim?: ClaimProcessingStatus | null;
    cpt?: ClaimProcessingStatus | null;
    implant?: ClaimProcessingStatus | null;
    cptQuantity?: ClaimProcessingStatus | null;
  }>({});

  useEffect(() => {
    if (!pollingFileId || !pollingData?.data) return;
    const file = pollingData.data;
    const prev = prevStatusesRef.current;

    const notifyTransition = (
      label: string,
      previous: ClaimProcessingStatus | null | undefined,
      current: ClaimProcessingStatus | null | undefined
    ) => {
      if (!current || current === previous) return;
      if (!isTerminal(current)) return;
      if (isTerminal(previous)) return;
      if (current === "completed") {
        toast.current?.show({
          severity: "success",
          summary: `${label} complete`,
        });
      } else if (current === "failed") {
        toast.current?.show({
          severity: "error",
          summary: `${label} failed`,
        });
      } else if (current === "skipped") {
        toast.current?.show({
          severity: "info",
          summary: `${label} skipped`,
        });
      }
    };

    // Fire a toast on the FIRST transition into a failed upload state. Same shape as the
    // notifyTransition helper above, but for the upload lifecycle rather than claim
    // post-processing.
    const prevUpload = prev.upload;
    if (
      file.uploadStatus &&
      file.uploadStatus !== prevUpload &&
      (file.uploadStatus === "failed_upload" ||
        file.uploadStatus === "failed_parse") &&
      prevUpload !== "failed_upload" &&
      prevUpload !== "failed_parse"
    ) {
      toast.current?.show({
        severity: "error",
        summary:
          file.uploadStatus === "failed_upload"
            ? "Upload failed"
            : "Parsing failed",
        detail:
          file.uploadStatus === "failed_upload"
            ? "The file did not finish uploading to storage."
            : "Parsing the uploaded file failed. See Sentry for details.",
        life: 10000,
      });
    }

    notifyTransition("Claims", prev.claim, file.claimProcessingStatus);
    notifyTransition("Common Code Pairs", prev.cpt, file.cptCoOccurrenceStatus);
    notifyTransition(
      "Common Implant Data",
      prev.implant,
      file.implantAssociationStatus
    );
    notifyTransition(
      "Common Code Quantity",
      prev.cptQuantity,
      file.cptCodeQuantityStatus
    );

    prevStatusesRef.current = {
      upload: file.uploadStatus,
      claim: file.claimProcessingStatus,
      cpt: file.cptCoOccurrenceStatus,
      implant: file.implantAssociationStatus,
      cptQuantity: file.cptCodeQuantityStatus,
    };

    // A failed upload or parse short-circuits the rest of the pipeline — the claim
    // processing stages stay 'pending' forever, so polling would otherwise loop.
    const uploadFailed =
      file.uploadStatus === "failed_upload" ||
      file.uploadStatus === "failed_parse";
    const allTerminal =
      uploadFailed ||
      (isTerminal(file.claimProcessingStatus) &&
        isTerminal(file.cptCoOccurrenceStatus) &&
        isTerminal(file.implantAssociationStatus) &&
        isTerminal(file.cptCodeQuantityStatus));
    if (allTerminal) {
      setPollingFileId(null);
      prevStatusesRef.current = {};
    }
  }, [pollingData, pollingFileId]);

  const resetForm = () => {
    setDisplayName("");
    setDescription("");
    setSelectedFiles([]);
    setSelectedFiles837([]);
    setMappings([newMappingForType("INSTITUTIONAL")]);
  };

  const handleFileSelect = (event: any) => {
    const newFiles = Array.from(event.files) as File[];
    setSelectedFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const unique = newFiles.filter(
        (f) => !existing.has(`${f.name}-${f.size}`)
      );
      return [...prev, ...unique];
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFile837Select = (event: any) => {
    const newFiles = Array.from(event.files) as File[];
    setSelectedFiles837((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const unique = newFiles.filter(
        (f) => !existing.has(`${f.name}-${f.size}`)
      );
      return [...prev, ...unique];
    });
  };

  const handleRemoveFile837 = (index: number) => {
    setSelectedFiles837((prev) => prev.filter((_, i) => i !== index));
  };

  const totalFileSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
  const totalFile837Size = selectedFiles837.reduce((sum, f) => sum + f.size, 0);

  const handleUpload = async () => {
    const completeMappings = mappings.filter((m) => m.providerId);
    if (
      !displayName.trim() ||
      selectedFiles.length === 0 ||
      completeMappings.length === 0
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          "Provide a display name, select at least one file, and add at least one claim-type → provider mapping",
      });
      return;
    }

    // The place-of-service sub-filter and the CLE-2437 taxonomy include-filter only apply to the
    // generic-provider rule; clear them elsewhere (the backend enforces this too).
    const normalizedMappings: IEdiUploadMapping[] = completeMappings.map(
      (m) => ({
        ...m,
        placeOfServiceFilter: posFilterAppliesTo(m.claimType)
          ? m.placeOfServiceFilter ?? []
          : [],
        excludeMidlevels: posFilterAppliesTo(m.claimType)
          ? m.excludeMidlevels ?? false
          : undefined,
        payerTaxonomyRules: posFilterAppliesTo(m.claimType)
          ? m.payerTaxonomyRules ?? []
          : undefined,
      })
    );

    // Final guard mirroring the backend (CLE-2424): cardinality + professional POS disjointness.
    const setError = validateEdiMappingSet(normalizedMappings);
    if (setError) {
      toast.current?.show({
        severity: "error",
        summary: "Invalid provider mappings",
        detail: setError,
      });
      return;
    }

    // Direct-to-S3 upload only applies when the user picks a single file (plus optionally a
    // single 837). Multi-file uploads still use /convert, which concatenates them server-side.
    // This isn't a limitation worth fixing yet — multi-file uploads are typically small
    // fragments well under the legacy 50MB cap; the direct-upload path exists for the big
    // single-zip clearinghouse case.
    const useDirectUpload =
      selectedFiles.length === 1 && selectedFiles837.length <= 1;

    setIsUploading(true);
    setUploadProgress(null);
    try {
      if (useDirectUpload) {
        const result = await directUpload({
          file: selectedFiles[0],
          file837: selectedFiles837[0],
          displayName: displayName.trim(),
          description: description.trim() || undefined,
          mappings: normalizedMappings,
          onProgress: setUploadProgress,
        });

        toast.current?.show({
          severity: "info",
          summary: "Upload complete — parsing started",
          detail:
            result.ediConversionFileIds.length > 1
              ? `Created ${result.ediConversionFileIds.length} conversion files (one per mapping). We'll let you know when each stage finishes.`
              : "We'll let you know when each stage finishes.",
        });
        if (result.ediConversionFileIds[0]) {
          setPollingFileId(result.ediConversionFileIds[0]);
        }
      } else {
        const result = await convertAndUpload({
          files: selectedFiles,
          displayName: displayName.trim(),
          description: description.trim() || undefined,
          mappings: normalizedMappings,
          files837: selectedFiles837.length > 0 ? selectedFiles837 : undefined,
        }).unwrap();

        // Show warnings from the server if any files were skipped or had issues
        if (result.warnings && result.warnings.length > 0) {
          toast.current?.show({
            severity: "warn",
            summary: "Upload Warnings",
            detail: result.warnings.join("; "),
            life: 8000,
          });
        }

        toast.current?.show({
          severity: "info",
          summary: "Processing started",
          detail:
            result.data.length > 1
              ? `Created ${result.data.length} conversion files (one per mapping). We'll let you know when each stage finishes.`
              : "We'll let you know when each stage finishes.",
        });
        const fileToPoll =
          result.data.find((f) => f.claimProcessingStatus !== "skipped") ??
          result.data[0];
        if (fileToPoll) setPollingFileId(fileToPoll.id);
      }

      resetForm();
      setUploadDialogVisible(false);
    } catch (error: any) {
      console.error("Upload error:", error);
      // The backend often returns a generic top-level `message` (e.g., "No valid EDI 835
      // files found") with the real reason in a `warnings` array (e.g., "Zip file is too
      // large (42.1MB)..."). Surface both so users can act on the actual failure.
      // The direct-upload path throws plain Errors from the helper; surface those too.
      const message =
        error?.data?.message ||
        error?.data?.messages?.[0] ||
        error?.message ||
        "Failed to convert and upload EDI file";
      const warnings: string[] | undefined = error?.data?.warnings;
      const detail =
        warnings && warnings.length > 0
          ? `${message} — ${warnings.join("; ")}`
          : message;
      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail,
        life: 10000,
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }edi-conversion/${id}/download`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      // URL is nested in data.data.downloadUrl based on backend response structure
      const downloadUrl =
        data?.data?.downloadUrl || data?.downloadUrl || data?.url;
      // CLE-2940 — which CSV the API served: "aggregated" for a file with no flat CSV.
      const kind: EdiCsvDownloadKind | undefined = data?.data?.kind;

      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
        if (kind === "aggregated") {
          toast.current?.show({
            severity: "info",
            summary: "Aggregated CSV",
            detail:
              "The flat CSV is not generated for files over 10,000 claims, so the aggregated CSV was downloaded.",
            life: 8000,
          });
        }
      } else {
        throw new Error("No download URL in response");
      }
    } catch (error: any) {
      console.error("Download error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error?.message || "Failed to download file",
      });
    }
  };

  const handleDownload837 = async (id: string) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }edi-conversion/${id}/download-837`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      const downloadUrl =
        data?.data?.downloadUrl || data?.downloadUrl || data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      } else {
        throw new Error("No download URL in response");
      }
    } catch (error: any) {
      console.error("Download 837 error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error?.message || "Failed to download 837 file",
      });
    }
  };

  const handleDownloadOriginal = async (id: string) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }edi-conversion/${id}/download-original`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      const downloadUrl =
        data?.data?.downloadUrl || data?.downloadUrl || data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      } else {
        throw new Error("No download URL in response");
      }
    } catch (error: any) {
      console.error("Download original 835 error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error?.message || "Failed to download original 835 file",
      });
    }
  };

  const handleDownloadOriginal837 = async (id: string) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }edi-conversion/${id}/download-original-837`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      const downloadUrl =
        data?.data?.downloadUrl || data?.downloadUrl || data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      } else {
        throw new Error("No download URL in response");
      }
    } catch (error: any) {
      console.error("Download original 837 error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error?.message || "Failed to download original 837 file",
      });
    }
  };

  const handleDelete = async (id: string, displayName: string) => {
    confirmDialog({
      message: `Are you sure you want to delete "${displayName}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteFile(id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Deleted",
            detail: "EDI conversion file deleted successfully",
          });
        } catch (error: any) {
          console.error("Delete error:", error);
          toast.current?.show({
            severity: "error",
            summary: "Delete Failed",
            detail: error?.data?.message || "Failed to delete file",
          });
        }
      },
    });
  };

  // Kick off a reprocess + start polling. Shared by the row "Reprocess" action and the
  // "Reprocess now?" prompt after an edit-mapping save (CLE-2479).
  const runReprocess = async (id: string) => {
    try {
      await reprocessFile(id).unwrap();
      toast.current?.show({
        severity: "info",
        summary: "Reprocess started",
      });
      setPollingFileId(id);
    } catch (error: any) {
      console.error("Reprocess error:", error);
      if (error?.status === 409) {
        toast.current?.show({
          severity: "warn",
          summary: "Already Running",
          detail: "Reprocessing is already in progress.",
        });
        setPollingFileId(id);
        return;
      }
      toast.current?.show({
        severity: "error",
        summary: "Reprocess Failed",
        detail: error?.data?.message || "Failed to reprocess.",
      });
    }
  };

  const handleReprocess = (id: string, displayName: string) => {
    confirmDialog({
      message: `Are you sure you want to reprocess "${displayName}"? This will delete all existing claims and regenerate them with current payer mappings and fee schedules.`,
      header: "Confirm Reprocess",
      icon: "pi pi-refresh",
      accept: () => runReprocess(id),
    });
  };

  // CLE-2921 — start the first-time parse of a held file (a split piece). Shared by the row action
  // here and "Process now" on a piece in the Split tab.
  const handleProcessHeld = async (id: string) => {
    try {
      await processHeldFile(id).unwrap();
      toast.current?.show({
        severity: "success",
        summary: "Processing started",
        detail: "We'll let you know when each stage finishes.",
      });
      setPollingFileId(id);
    } catch (error: any) {
      console.error("Process held file error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Could not start processing",
        detail:
          error?.data?.message ||
          (error?.status === 409
            ? "This file is not on hold any more."
            : "Failed to start processing."),
      });
    }
  };

  const openSplitDialog = (file: IEdiConversionFile | null) => {
    setSplitInitialFile(file);
    setSplitDialogVisible(true);
  };

  // CLE-2479 — after the mapping is saved, prompt to reprocess so the change actually reaches the
  // claims (they keep the old provider/pricing until the file is reprocessed).
  const handleMappingSaved = (fileId: string, fileDisplayName: string) => {
    toast.current?.show({
      severity: "success",
      summary: "Mapping updated",
      detail: `"${fileDisplayName}" mapping saved.`,
    });
    confirmDialog({
      message: `Reprocess "${fileDisplayName}" now to apply the new mapping to its claims? Existing claims keep the old mapping until you reprocess.`,
      header: "Reprocess now?",
      icon: "pi pi-refresh",
      acceptLabel: "Reprocess now",
      rejectLabel: "Later",
      accept: () => runReprocess(fileId),
    });
  };

  const handleRunAiAnalysis = async (id: string, displayName: string) => {
    confirmDialog({
      message: `Are you sure you want to run AI analysis for all claims in "${displayName}"? This will overwrite any existing AI analysis results.`,
      header: "Confirm AI Analysis",
      icon: "pi pi-bolt",
      accept: async () => {
        try {
          const result = await runAiAnalysis(id).unwrap();
          toast.current?.show({
            severity: "info",
            summary: "AI Analysis Started",
            detail: `AI analysis started for ${result.data.claimCount} claims. This may take a few minutes.`,
          });
        } catch (error: any) {
          console.error("AI analysis error:", error);
          toast.current?.show({
            severity: "error",
            summary: "AI Analysis Failed",
            detail: error?.data?.message || "Failed to start AI analysis",
          });
        }
      },
    });
  };

  const dateBodyTemplate = (rowData: IEdiConversionFile) => {
    return parseDate(rowData.createdAt);
  };

  // CLE-2415 — "Last Processed" shows the most recent re-process time, or "N/A" for files that
  // have never been re-processed (lastReprocessedAt is null until reprocessEdiFile runs).
  const lastProcessedBodyTemplate = (rowData: IEdiConversionFile) => {
    return rowData.lastReprocessedAt
      ? parseDate(rowData.lastReprocessedAt)
      : "N/A";
  };

  const recordCountBodyTemplate = (rowData: IEdiConversionFile) => {
    // A held piece has not been parsed, so its 0 is "not counted yet", not "empty".
    if (isHeld(rowData)) {
      return <span className="text-500">—</span>;
    }
    return rowData.recordCount.toLocaleString();
  };

  const convertedByBodyTemplate = (rowData: IEdiConversionFile) => {
    // Handle if convertedBy is an object with name property
    if (
      typeof rowData.convertedBy === "object" &&
      rowData.convertedBy !== null
    ) {
      return (
        (rowData.convertedBy as any).name ||
        (rowData.convertedBy as any).email ||
        "Unknown"
      );
    }
    return rowData.convertedBy || "Unknown";
  };

  const providerBodyTemplate = (rowData: IEdiConversionFile) => {
    if (!rowData.providerId) {
      return <span className="text-500">-</span>;
    }

    // Find provider name from the list
    const provider = providers?.data?.find(
      (p: any) => p.id === rowData.providerId
    );
    return provider?.name || rowData.providerId;
  };

  const aggregateStatus = (
    file: IEdiConversionFile
  ): ClaimProcessingStatus | null => {
    const all = [
      file.claimProcessingStatus,
      file.cptCoOccurrenceStatus,
      file.implantAssociationStatus,
      file.cptCodeQuantityStatus,
    ].filter((s): s is ClaimProcessingStatus => !!s);
    if (all.length === 0) return null;
    if (all.some((s) => s === "failed")) return "failed";
    if (all.some((s) => s === "processing")) return "processing";
    if (all.some((s) => s === "pending")) return "pending";
    if (all.every((s) => s === "skipped")) return "skipped";
    return "completed";
  };

  const statusBodyTemplate = (rowData: IEdiConversionFile) => (
    <EdiFileStatusLabel
      status={isHeld(rowData) ? "held" : aggregateStatus(rowData)}
    />
  );

  const actionsBodyTemplate = (rowData: IEdiConversionFile) => {
    const status = rowData.claimProcessingStatus;
    // CLE-2921 — a held file (a split piece waiting to be processed) has no claims and no CSV yet.
    const held = isHeld(rowData);
    const reprocessing = pollingFileId === rowData.id;
    const reprocessable =
      !held && !reprocessing && (status === "completed" || status === "failed");
    let reprocessTooltip: string | undefined;
    if (held) {
      reprocessTooltip = HELD_TOOLTIP;
    } else if (reprocessing) {
      reprocessTooltip = "Reprocessing…";
    } else if (reprocessable) {
      reprocessTooltip = "Reprocess";
    }
    const analysable = !held && status === "completed";
    let aiTooltip: string | undefined;
    if (held) {
      aiTooltip = HELD_TOOLTIP;
    } else if (analysable) {
      aiTooltip = "Run AI analysis";
    }

    // CLE-2479 — allow editing the mapping once the file is no longer actively processing.
    const editable = !reprocessing && isTerminal(status);

    // CLE-2940 — a file with no flat CSV downloads its aggregated CSV from the same button.
    let csvTooltip = CSV_TOOLTIP;
    if (held) {
      csvTooltip = HELD_TOOLTIP;
    } else if (isFlatCsvSkipped(rowData)) {
      csvTooltip = AGGREGATED_CSV_TOOLTIP;
    }

    // CLE-2921 — split a fully processed file into smaller files.
    const splitting = splittingFileIds.has(rowData.id);
    const splittable = !splitting && canSplitEdiFile(rowData);
    let splitTooltip = "Split into smaller files";
    if (splitting) {
      splitTooltip = "A split of this file is already running";
    } else if (!splittable) {
      splitTooltip = rowData.providerId
        ? "Split into smaller files — available once the file has finished processing"
        : "Split into smaller files — needs a provider mapping";
    }

    return (
      <div style={{ display: "flex", gap: "0.125rem", alignItems: "center" }}>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={editable ? () => setEditFile(rowData) : undefined}
          tooltip={editable ? "Edit mapping" : undefined}
          tooltipOptions={{ position: "top" }}
          disabled={!editable}
        />
        {held && (
          <Button
            icon="pi pi-play"
            className="p-button-rounded p-button-text p-button-success"
            aria-label="Process now"
            onClick={() => handleProcessHeld(rowData.id)}
            tooltip="Process now"
            tooltipOptions={{ position: "top" }}
          />
        )}
        <Button
          icon={reprocessing ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
          className="p-button-rounded p-button-text"
          style={{ color: "#f59e0b" }}
          aria-label="Reprocess"
          onClick={
            reprocessable
              ? () => handleReprocess(rowData.id, rowData.displayName)
              : undefined
          }
          tooltip={reprocessTooltip}
          tooltipOptions={{ position: "top", showOnDisabled: held }}
          disabled={!reprocessable}
        />
        <Button
          icon="pi pi-sitemap"
          className="p-button-rounded p-button-text"
          aria-label="Split into smaller files"
          onClick={splittable ? () => openSplitDialog(rowData) : undefined}
          tooltip={splitTooltip}
          tooltipOptions={{ position: "top", showOnDisabled: true }}
          disabled={!splittable}
        />
        <Button
          icon="pi pi-bolt"
          className="p-button-rounded p-button-text p-button-help"
          aria-label="Run AI analysis"
          onClick={
            analysable
              ? () => handleRunAiAnalysis(rowData.id, rowData.displayName)
              : undefined
          }
          tooltip={aiTooltip}
          tooltipOptions={{ position: "top", showOnDisabled: held }}
          disabled={!analysable}
        />
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text"
          aria-label="Download 835 CSV"
          onClick={held ? undefined : () => handleDownload(rowData.id)}
          tooltip={csvTooltip}
          tooltipOptions={{ position: "top", showOnDisabled: held }}
          disabled={held}
        />
        {rowData.has837 && (
          <Button
            icon="pi pi-download"
            className="p-button-rounded p-button-text p-button-info"
            onClick={() => handleDownload837(rowData.id)}
            tooltip="Download 837 CSV"
            tooltipOptions={{ position: "top" }}
          />
        )}
        <Button
          icon="pi pi-file"
          className="p-button-rounded p-button-text"
          onClick={() => handleDownloadOriginal(rowData.id)}
          tooltip="Download original 835"
          tooltipOptions={{ position: "top" }}
        />
        {rowData.has837 && (
          <Button
            icon="pi pi-file"
            className="p-button-rounded p-button-text p-button-info"
            onClick={() => handleDownloadOriginal837(rowData.id)}
            tooltip="Download original 837"
            tooltipOptions={{ position: "top" }}
          />
        )}
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDelete(rowData.id, rowData.displayName)}
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  // Progress bar copy. uploadProgress is only set while we're PUTting bytes to S3 (direct-upload
  // path). For the legacy /convert path we just show the spinning button label.
  const progressPercent =
    uploadProgress && uploadProgress.total > 0
      ? Math.min(
          100,
          Math.round((uploadProgress.loaded / uploadProgress.total) * 100)
        )
      : 0;
  const progressLabel = uploadProgress
    ? `Uploading ${uploadProgress.currentLabel}… ${progressPercent}%`
    : null;

  const dialogFooter = (
    <div>
      {uploadProgress && (
        <div className="mb-2">
          <div className="text-sm mb-1">{progressLabel}</div>
          <ProgressBar value={progressPercent} showValue={false} />
        </div>
      )}
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => {
          resetForm();
          setUploadDialogVisible(false);
        }}
        disabled={isUploading}
      />
      <Button
        label={isUploading ? "Uploading..." : "Convert & Upload"}
        icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
        onClick={handleUpload}
        disabled={
          isUploading ||
          selectedFiles.length === 0 ||
          !displayName.trim() ||
          !mappings.some((m) => m.providerId) ||
          !!mappingError
        }
      />
    </div>
  );

  // CLE-2479 — the other rows in the edited file's upload batch. Cardinality/POS-disjointness is
  // enforced across the whole batch, so the edit dialog validates against these (and disables their
  // already-used claim types). Only claimType + placeOfServiceFilter matter to that validation.
  const editSiblingMappings: IEdiUploadMapping[] = editFile
    ? ediConversionFiles
        .filter(
          (f) =>
            !!f.uploadBatchId &&
            f.uploadBatchId === editFile.uploadBatchId &&
            f.id !== editFile.id
        )
        .map((f) => ({
          claimType: (f.claimType ?? "PROFESSIONAL_PROVIDER") as EdiClaimType,
          providerId: f.providerId ?? "",
          placeOfServiceFilter: f.metadata?.placeOfServiceFilter ?? [],
        }))
    : [];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Toast ref={toast} />

      <TabView
        activeIndex={activeTabIndex}
        onTabChange={(e) => setActiveTabIndex(e.index)}
      >
        <TabPanel header="835 Conversion Files">
          <div className="flex justify-content-between align-items-center mb-3">
            <div className="flex align-items-center gap-3">
              <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search files..."
                  className="w-20rem"
                />
              </span>
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId="show-two-files-only"
                  checked={showTwoFilesOnly}
                  onChange={(e) => handleToggleShowTwo(e.checked ?? false)}
                />
                <label
                  htmlFor="show-two-files-only"
                  style={{ whiteSpace: "nowrap", cursor: "pointer" }}
                >
                  Show only 2 Office-rate files
                </label>
              </div>
            </div>
            <div className="flex align-items-center gap-2">
              <Button
                label="Reprocess all for provider"
                icon="pi pi-refresh"
                className="p-button-outlined"
                onClick={() => setReprocessAllVisible(true)}
              />
              <Button
                label="Upload 835 File"
                icon="pi pi-plus"
                className="p-button-raised"
                onClick={() => setUploadDialogVisible(true)}
              />
            </div>
          </div>

          <ReprocessAllModal
            visible={reprocessAllVisible}
            onHide={() => setReprocessAllVisible(false)}
            toast={toast}
          />

          <DataTable
            value={visibleFiles}
            paginator
            rows={10}
            stripedRows
            size="small"
            responsiveLayout="scroll"
            emptyMessage="No EDI conversion files found."
          >
            <Column
              field="providerId"
              header="Provider"
              sortable
              body={providerBodyTemplate}
              style={{ minWidth: "7rem" }}
            />
            <Column
              field="displayName"
              header="Display Name"
              sortable
              style={{ minWidth: "8rem" }}
            />
            <Column
              field="fileName"
              header="File Name"
              sortable
              style={{ minWidth: "9rem" }}
            />
            <Column
              field="convertedBy"
              header="Converted By"
              sortable
              body={convertedByBodyTemplate}
              style={{ minWidth: "7rem" }}
            />
            <Column
              field="createdAt"
              header="Date"
              sortable
              body={dateBodyTemplate}
              style={{ minWidth: "6.5rem" }}
            />
            <Column
              field="lastReprocessedAt"
              header="Last Processed"
              sortable
              body={lastProcessedBodyTemplate}
              style={{ minWidth: "6.5rem" }}
            />
            <Column
              field="recordCount"
              header="Records"
              sortable
              body={recordCountBodyTemplate}
              style={{ minWidth: "5rem" }}
            />
            <Column
              field="claimProcessingStatus"
              header="Status"
              sortable
              body={statusBodyTemplate}
              style={{ minWidth: "8rem" }}
            />
            <Column
              body={actionsBodyTemplate}
              header="Actions"
              style={{ minWidth: "18rem", width: "18rem" }}
            />
          </DataTable>
        </TabPanel>
        <TabPanel header="837 Submissions">
          <Edi837IngestsTable />
        </TabPanel>
        <TabPanel header="Split 835 Files">
          <EdiSplitFilesTab
            toast={toast}
            onSplitFile={() => openSplitDialog(null)}
            onProcessHeldFile={handleProcessHeld}
            onDownloadOriginal={handleDownloadOriginal}
            onDeleteFile={handleDelete}
          />
        </TabPanel>
      </TabView>

      <SplitEdiFileDialog
        visible={splitDialogVisible}
        onHide={() => setSplitDialogVisible(false)}
        initialFile={splitInitialFile}
        toast={toast}
        onSplitStarted={() => setActiveTabIndex(SPLIT_TAB_INDEX)}
      />

      <StyledDialog
        header="Upload & Convert EDI File"
        visible={uploadDialogVisible}
        onHide={() => {
          if (!isUploading) {
            resetForm();
            setUploadDialogVisible(false);
          }
        }}
        footer={dialogFooter}
        style={{ width: "min(960px, 95vw)" }}
        modal
        closable={!isUploading}
        blockScroll
      >
        <FormField>
          <label>
            Display Name <span className="required">*</span>
          </label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Bluecross Claims Batch A"
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField>
          <label>Description</label>
          <InputTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            rows={3}
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <label>
            Claim type → provider mappings <span className="required">*</span>
          </label>
          <small
            className="text-muted"
            style={{ display: "block", marginBottom: "0.75rem" }}
          >
            Each rule routes a slice of the uploaded claims to a provider. The
            provider list is filtered to the specialty the type requires. Claims
            that match no rule are not imported. Institutional and Anesthesia
            map to one provider each; Professional — Provider can be split
            across providers by place of service.
          </small>

          {mappings.map((m, idx) => {
            const usedTypes = mappings
              .filter((_, i) => i !== idx)
              .map((x) => x.claimType);
            const typeOptions = EDI_CLAIM_TYPE_OPTIONS.map((o) => ({
              ...o,
              // PROFESSIONAL_PROVIDER may repeat (POS-split, CLE-2424); other types map once.
              disabled:
                o.value !== "PROFESSIONAL_PROVIDER" &&
                usedTypes.includes(o.value),
            }));
            const allowed = allowedSpecialtiesForClaimType(m.claimType);
            const providerOpts = (providers?.data ?? [])
              .filter((p: IProvider) => allowed.includes(p.specialty))
              .map((p: IProvider) => ({ label: p.name, value: p.id }));
            const showPos = posFilterAppliesTo(m.claimType);
            const posFilter = m.placeOfServiceFilter ?? [];
            // CLE-2437 — taxonomy include-filter state for this professional mapping.
            const taxRules = m.payerTaxonomyRules ?? [];
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 4,
                  padding: "0.75rem",
                  marginBottom: "0.75rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "flex-start",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Dropdown
                      value={m.claimType}
                      options={typeOptions}
                      optionDisabled="disabled"
                      onChange={(e) =>
                        updateMapping(idx, {
                          ...newMappingForType(e.value as EdiClaimType),
                        })
                      }
                      className="w-full"
                      disabled={isUploading}
                      appendTo="self"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Dropdown
                      value={m.providerId}
                      options={providerOpts}
                      onChange={(e) =>
                        updateMapping(idx, { providerId: e.value })
                      }
                      placeholder="Select provider"
                      className="w-full"
                      filter
                      showClear
                      disabled={isUploading}
                      appendTo="self"
                    />
                  </div>
                  {mappings.length > 1 && (
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-danger"
                      type="button"
                      disabled={isUploading}
                      aria-label="Remove mapping"
                      onClick={() => removeMapping(idx)}
                    />
                  )}
                </div>
                {showPos && (
                  <div style={{ marginTop: "0.5rem" }}>
                    <small
                      className="text-muted"
                      style={{ display: "block", marginBottom: "0.25rem" }}
                    >
                      Place of service (leave both unchecked to keep all):
                    </small>
                    <div style={{ display: "flex", gap: "1rem" }}>
                      {[
                        {
                          key: "facility",
                          label: "Facility",
                          cats: FACILITY_POS_CATEGORIES,
                        },
                        {
                          key: "non-facility",
                          label: "Non-Facility",
                          cats: NON_FACILITY_POS_CATEGORIES,
                        },
                      ].map((opt) => {
                        const checked = opt.cats.some((c) =>
                          posFilter.includes(c)
                        );
                        return (
                          <div
                            key={opt.key}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.25rem",
                            }}
                          >
                            <Checkbox
                              inputId={`pos-${idx}-${opt.key}`}
                              checked={checked}
                              disabled={isUploading}
                              onChange={(e) => {
                                const without = posFilter.filter(
                                  (c) => !opt.cats.includes(c)
                                );
                                updateMapping(idx, {
                                  placeOfServiceFilter: e.checked
                                    ? [...without, ...opt.cats]
                                    : without,
                                });
                              }}
                            />
                            <label htmlFor={`pos-${idx}-${opt.key}`}>
                              {opt.label}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* CLE-2437 — rendering-taxonomy include-filter (professional mappings only).
                    Opt-in: leave everything off to price every professional claim as before. */}
                {showPos && (
                  <div
                    style={{
                      marginTop: "0.75rem",
                      paddingTop: "0.75rem",
                      borderTop: "1px solid var(--surface-200, #e5e7eb)",
                    }}
                  >
                    <small
                      className="text-muted"
                      style={{ display: "block", marginBottom: "0.5rem" }}
                    >
                      Specialty (taxonomy) filter — optional. Leave unconfigured
                      to price every professional claim.
                    </small>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <Checkbox
                        inputId={`exclude-midlevels-${idx}`}
                        checked={m.excludeMidlevels ?? false}
                        disabled={isUploading}
                        onChange={(e) =>
                          updateMapping(idx, {
                            excludeMidlevels: e.checked ?? false,
                          })
                        }
                      />
                      <label htmlFor={`exclude-midlevels-${idx}`}>
                        Exclude mid-levels (NP / PA / Audiology / LCSW)
                      </label>
                    </div>

                    {taxRules.map((rule, ruleIdx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={ruleIdx}
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginBottom: "0.5rem",
                          padding: "0.5rem",
                          border: "1px solid var(--surface-200, #e5e7eb)",
                          borderRadius: "4px",
                        }}
                      >
                        <MultiSelect
                          value={rule.payerIds}
                          options={payerOptions}
                          disabled={isUploading}
                          filter
                          display="chip"
                          placeholder="Select payer(s)"
                          style={{ minWidth: "14rem" }}
                          onChange={(e) => {
                            const next = [...taxRules];
                            next[ruleIdx] = {
                              ...rule,
                              payerIds: e.value as number[],
                            };
                            updateTaxonomyRules(idx, next);
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                          }}
                        >
                          <Checkbox
                            inputId={`taxonomy-include-all-${idx}-${ruleIdx}`}
                            checked={rule.includeAll}
                            disabled={isUploading}
                            onChange={(e) => {
                              const next = [...taxRules];
                              next[ruleIdx] = {
                                ...rule,
                                includeAll: e.checked ?? false,
                                // Clear the explicit list when switching to include-all.
                                taxonomies: e.checked ? [] : rule.taxonomies,
                              };
                              updateTaxonomyRules(idx, next);
                            }}
                          />
                          <label
                            htmlFor={`taxonomy-include-all-${idx}-${ruleIdx}`}
                          >
                            Include all
                          </label>
                        </div>
                        <MultiSelect
                          value={rule.taxonomies}
                          options={taxonomyOptions}
                          disabled={isUploading || rule.includeAll}
                          filter
                          display="chip"
                          placeholder="Include taxonomies"
                          style={{ minWidth: "16rem" }}
                          onChange={(e) => {
                            const next = [...taxRules];
                            next[ruleIdx] = {
                              ...rule,
                              taxonomies: e.value as string[],
                            };
                            updateTaxonomyRules(idx, next);
                          }}
                        />
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          className="p-button-text p-button-danger p-button-sm"
                          disabled={isUploading}
                          aria-label="Remove taxonomy rule"
                          onClick={() =>
                            updateTaxonomyRules(
                              idx,
                              taxRules.filter((_, i) => i !== ruleIdx)
                            )
                          }
                        />
                      </div>
                    ))}

                    <Button
                      type="button"
                      label="Add payer taxonomy rule"
                      icon="pi pi-plus"
                      className="p-button-sm p-button-text"
                      disabled={isUploading}
                      onClick={() =>
                        updateTaxonomyRules(idx, [
                          ...taxRules,
                          { payerIds: [], includeAll: false, taxonomies: [] },
                        ])
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}

          {mappingError && (
            <small
              className="p-error"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {mappingError}
            </small>
          )}

          {mappings.length < MAX_MAPPINGS && (
            <Button
              label="Add mapping"
              icon="pi pi-plus"
              className="p-button-sm p-button-outlined"
              type="button"
              disabled={isUploading}
              onClick={addMapping}
            />
          )}
        </FormField>

        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
          <FormField style={{ flex: "1 1 280px" }}>
            <label>
              835 Remittance Files (.txt, .edi, .835, .zip){" "}
              <span className="required">*</span>
            </label>
            {selectedFiles.length > 0 && (
              <FileList>
                {selectedFiles.map((file, index) => (
                  <FileInfo key={`${file.name}-${index}`}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div className="file-name">{file.name}</div>
                        <div className="file-details">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <Button
                        icon="pi pi-times"
                        className="p-button-text p-button-sm p-button-danger"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isUploading}
                        tooltip="Remove file"
                      />
                    </div>
                  </FileInfo>
                ))}
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#6c757d",
                    marginTop: "0.25rem",
                  }}
                >
                  {selectedFiles.length} file
                  {selectedFiles.length !== 1 ? "s" : ""} selected (
                  {(totalFileSize / 1024).toFixed(1)} KB total)
                </div>
              </FileList>
            )}
            <FileUpload
              mode="basic"
              name="files"
              accept=".txt,.edi,.835,.zip"
              maxFileSize={30000000}
              chooseLabel={
                selectedFiles.length > 0 ? "Add More Files" : "Choose 835 Files"
              }
              className="w-full"
              onSelect={handleFileSelect}
              disabled={isUploading}
              multiple
            />
            <small className="text-muted">
              Supported formats: .txt, .edi, .835, .zip (Max total size: 50MB)
            </small>
          </FormField>

          <FormField style={{ flex: "1 1 280px" }}>
            <label>837 Claim Files (Optional)</label>
            {selectedFiles837.length > 0 && (
              <FileList>
                {selectedFiles837.map((file, index) => (
                  <FileInfo key={`837-${file.name}-${index}`}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div className="file-name">{file.name}</div>
                        <div className="file-details">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <Button
                        icon="pi pi-times"
                        className="p-button-text p-button-sm p-button-danger"
                        onClick={() => handleRemoveFile837(index)}
                        disabled={isUploading}
                        tooltip="Remove file"
                      />
                    </div>
                  </FileInfo>
                ))}
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#6c757d",
                    marginTop: "0.25rem",
                  }}
                >
                  {selectedFiles837.length} file
                  {selectedFiles837.length !== 1 ? "s" : ""} selected (
                  {(totalFile837Size / 1024).toFixed(1)} KB total)
                </div>
              </FileList>
            )}
            <FileUpload
              mode="basic"
              name="files837"
              accept=".txt,.edi,.837,.zip"
              maxFileSize={30000000}
              chooseLabel={
                selectedFiles837.length > 0
                  ? "Add More 837 Files"
                  : "Choose 837 Files"
              }
              className="w-full"
              onSelect={handleFile837Select}
              disabled={isUploading}
              multiple
            />
            <small className="text-muted">
              Optional: Upload matching 837 claim files for inpatient/outpatient
              classification (.txt, .edi, .837, .zip)
            </small>
          </FormField>
        </div>
      </StyledDialog>

      <EditMappingModal
        file={editFile}
        visible={!!editFile}
        onHide={() => setEditFile(null)}
        providers={providers?.data ?? []}
        payerOptions={payerOptions}
        taxonomyOptions={taxonomyOptions}
        siblingMappings={editSiblingMappings}
        onSaved={handleMappingSaved}
        onError={(message) =>
          toast.current?.show({
            severity: "error",
            summary: "Update Failed",
            detail: message,
          })
        }
      />
    </>
  );
}

export default EdiConversionFilesTable;
